import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { RENDER_DEBUG } from "@/lib/debug-flags";
import { messageScreenQueries, queries } from "@/lib/queries";
import { useRenderUserMap } from "@/lib/selectors";
import { useWorkspacePublicState } from "@/lib/selectors/use-is-public-workspace";
import { useIsThreadCurator } from "@/lib/selectors/use-thread-curator";
import { useCurrentUser } from "@/lib/current-user";
import { mutators } from "@/lib/mutators";
import { type ThreadQaPayload } from "@ano/shared/mutators";
import { MessageInput } from "./MessageInput";
import { useThreadPaneWidth } from "./use-thread-pane-width";
import { cn } from "@/lib/utils";
import { useSingleOpenMessageAction } from "./use-single-open-message-action";
import {
  acceptedReplyExists as computeAcceptedReplyExists,
  orderThreadReplies,
} from "./thread-qa-ordering";
import {
  ThreadPanelHeader,
  ThreadParentMessage,
  ThreadReplyList,
} from "./ThreadPanel.parts";

interface ThreadPanelProps {
  parentMessageId: string;
  channelId: string;
  workspaceMembers: { id: string; display_name: string }[];
  channels?: { id: string; name: string }[];
  /** Active workspace-member user IDs. `null` for DM channels. */
  activeWorkspaceMemberIds: Set<string> | null;
  onClose: () => void;
  onScrollToParent?: () => void;
  onUserClick?: (userId: string) => void;
  onChannelClick?: (channelId: string) => void;
  onSendToShell?: (
    messageId: string,
    selection?: string,
    msgOverride?: {
      id: string;
      content: string;
      user_id: string;
      thread_id?: string | null;
      created_at: number;
    },
  ) => void;
  isCoworkerDm?: boolean;
  coworkerIds?: Set<string>;
  /** Reply id to scroll to + highlight on mount (notification deep-link). */
  scrollToMessageId?: string | null;
  /** Called once the scroll has been applied, so the parent can clear the intent. */
  onScrollHandled?: () => void;
  /** True when the hosting MessageArea is multiplexed and not active.
   *  Threaded into the inner MessageInput so its document-level keydown
   *  listener doesn't steal keystrokes from the active composer. */
  hidden?: boolean;
}

export function ThreadPanel({
  parentMessageId,
  channelId,
  workspaceMembers,
  channels,
  activeWorkspaceMemberIds,
  onClose,
  onScrollToParent,
  onUserClick,
  onChannelClick,
  onSendToShell,
  isCoworkerDm,
  coworkerIds,
  scrollToMessageId,
  onScrollHandled,
  hidden = false,
}: ThreadPanelProps) {
  // ─── Render diagnostics ─────────────────────────────────────────
  const renderCountRef = useRef(0);
  // eslint-disable-next-line react-hooks/refs
  renderCountRef.current += 1;

  const zero = useZero();
  const { user } = useCurrentUser();
  const userId = user?.id ?? null;
  // Phase 3 — single composite subscription replaces three separate
  // useQuery calls (threadParent + threadReplies + thread_followers).
  // The composite returns the parent message with its full child-table
  // relations + nested `thread_replies` (each with their own children) +
  // `thread_followers`. ViewCache refcount-shares this materialization
  // across all four `useChannelMessages`-family hooks for the same
  // (channelId, limit) — but ThreadPanel uses its own thread-scoped key
  // so it doesn't collide with channel-render subs.
  const [threadComposite] = useQuery(
    messageScreenQueries.threadComposite({ threadId: parentMessageId }),
  );
  const parentMessage = threadComposite ?? null;
  // Discord-shape — thread sidebar shows ONLY thread replies (rows with
  // `thread_id = parent.id`). Inline replies live in the channel
  // timeline as free-standing messages and are NOT re-shown here.
  const threadReplies = useMemo(
    () => threadComposite?.thread_replies ?? [],
    [threadComposite],
  );
  const threadFollowers = useMemo(
    () => threadComposite?.thread_followers ?? [],
    [threadComposite],
  );

  // Q&A (public-community channels only): thread title + accepted-answer
  // (solved) state live on the root message's payload. The asker may curate;
  // the server re-checks (mutators/thread-qa.ts).
  const [qaChannel] = useQuery(queries.channels.byId(channelId));
  const { isPublic: isPublicWs, isResolved: wsResolved } =
    useWorkspacePublicState(qaChannel?.workspace_id);
  const isAsker = !!userId && parentMessage?.user_id === userId;
  // The asker, a channel manager, or a workspace admin may curate — mirror the
  // server's assertThreadQaAuth so the UI shows controls to exactly whom the
  // mutator will accept (the previous asker-only gate hid them from admins).
  const isCurator = useIsThreadCurator(
    qaChannel?.workspace_id,
    channelId,
    userId,
  );
  const canCurate = isPublicWs && (isAsker || isCurator);
  // Threads are the primary content in public communities — wider + resizable.
  const pane = useThreadPaneWidth(480);
  const rootPayload = (parentMessage?.payload ??
    null) as ThreadQaPayload | null;
  const threadTitle: string | null = rootPayload?.thread_title ?? null;
  const acceptedAnswerId: string | null =
    rootPayload?.accepted_answer_id ?? null;
  // The accepted answer can be HARD-deleted out from under the payload, leaving
  // a dangling id. Only treat the thread as solved / float the answer when the
  // referenced reply actually still exists (and only in public workspaces).
  const acceptedReplyExists = computeAcceptedReplyExists(
    acceptedAnswerId,
    threadReplies,
  );
  const isSolved = isPublicWs && acceptedReplyExists;
  const orderedReplies = useMemo(
    () => orderThreadReplies(threadReplies, acceptedAnswerId, isPublicWs),
    [threadReplies, acceptedAnswerId, isPublicWs],
  );

  if (RENDER_DEBUG)
    console.debug(
      // eslint-disable-next-line react-hooks/refs
      `[ThreadPanel] render #${renderCountRef.current}`,
      `parent=${parentMessageId.slice(-6)} ch=${channelId.slice(-6)} replies=${threadReplies.length}`,
    );
  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const {
    isMessageOpen,
    setMessageOpen,
    closeAll: closeAllActionPills,
  } = useSingleOpenMessageAction();
  const [sharedUserMap] = useRenderUserMap();
  const followerByUserId = useMemo(() => {
    const map = new Map<string, (typeof threadFollowers)[number]>();
    for (const follower of threadFollowers) {
      map.set(follower.user_id, follower);
    }
    return map;
  }, [threadFollowers]);

  // Looks up the reply (or parent) targeted by Send-to-Shell and forwards
  // it via the `msgOverride` param — replies aren't in the channel message
  // list so the parent's default `messageById` lookup can't find them.
  const handleThreadSendToShell = useCallback(
    (id: string) => {
      if (!onSendToShell) return;
      const msg =
        id === parentMessageId
          ? parentMessage
          : threadReplies.find((r) => r.id === id);
      if (!msg) return;
      onSendToShell(id, undefined, {
        id: msg.id,
        content: msg.content,
        user_id: msg.user_id,
        thread_id: msg.thread_id ?? null,
        created_at: msg.created_at,
      });
    },
    [onSendToShell, parentMessageId, parentMessage, threadReplies],
  );

  const isFollowing = useMemo(
    () => (userId ? followerByUserId.has(userId) : false),
    [followerByUserId, userId],
  );

  const handleToggleFollow = () => {
    if (!user || !parentMessage) return;

    if (isFollowing) {
      const follower = followerByUserId.get(user.id);
      if (follower) {
        zero.mutate(mutators.thread_followers.delete({ id: follower.id }));
      }
    } else {
      zero.mutate(
        mutators.thread_followers.insert({
          id: crypto.randomUUID(),
          thread_id: parentMessageId,
          user_id: user.id,
          followed_at: Date.now(),
        }),
      );
    }
  };

  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
    }
  }, [threadReplies.length]);

  useEffect(() => {
    closeAllActionPills();
  }, [parentMessageId, closeAllActionPills]);

  // ─── Deep-link scroll: scroll to a specific reply + briefly highlight ──
  const [highlightedReplyId, setHighlightedReplyId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!scrollToMessageId) return;
    // Only act once the reply row exists in the loaded set — Zero may still
    // be hydrating. The effect re-fires on each replies update until we find it.
    const exists = threadReplies.some((r) => r.id === scrollToMessageId);
    if (!exists) return;

    const raf = requestAnimationFrame(() => {
      const el = scrollRef.current?.querySelector<HTMLElement>(
        `[data-message-id="${CSS.escape(scrollToMessageId)}"]`,
      );
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "auto" });
      // Don't let the auto-bottom effect yank us away when new replies arrive.
      isNearBottomRef.current = false;
      setHighlightedReplyId(scrollToMessageId);
      onScrollHandled?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollToMessageId, threadReplies, onScrollHandled]);

  useEffect(() => {
    if (!highlightedReplyId) return;
    const t = setTimeout(() => setHighlightedReplyId(null), 2000);
    return () => clearTimeout(t);
  }, [highlightedReplyId]);

  if (!parentMessage) return null;

  return (
    <div
      className={cn(
        "relative flex flex-col border-l border-border bg-background",
        // Only commit to the narrow private width once we KNOW it isn't a
        // public community — otherwise the pane paints at 320px then snaps
        // wider when the workspace query lands. Default the unresolved frame
        // to the wide resizable width (the feature's primary surface).
        wsResolved && !isPublicWs && "w-80",
        pane.dragging && "select-none",
      )}
      style={!wsResolved || isPublicWs ? { width: pane.width } : undefined}
    >
      {isPublicWs && (
        <div
          onPointerDown={pane.onPointerDown}
          className="absolute left-0 top-0 z-20 h-full w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-primary/40"
          title="Drag to resize"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize thread panel"
        />
      )}
      <div className="h-[38px] shrink-0" />
      <ThreadPanelHeader
        parentMessageId={parentMessageId}
        isPublicWs={isPublicWs}
        threadTitle={threadTitle}
        isSolved={isSolved}
        canCurate={canCurate}
        acceptedAnswerId={acceptedAnswerId}
        onScrollToParent={onScrollToParent}
        followerCount={threadFollowers.length}
        isFollowing={isFollowing}
        onToggleFollow={handleToggleFollow}
        onClose={onClose}
      />

      <div
        ref={scrollRef}
        onScroll={() => {
          const el = scrollRef.current;
          if (el) {
            isNearBottomRef.current =
              el.scrollHeight - el.scrollTop - el.clientHeight < 80;
          }
        }}
        className="flex-1 overflow-auto p-4 space-y-0.5"
      >
        <ThreadParentMessage
          parentMessage={parentMessage}
          sharedUserMap={sharedUserMap}
          workspaceMembers={workspaceMembers}
          channels={channels}
          onUserClick={onUserClick}
          onChannelClick={onChannelClick}
          onSendToShell={onSendToShell ? handleThreadSendToShell : undefined}
          actionPillOpen={isMessageOpen(parentMessage.id)}
          onActionPillOpenChange={(open) =>
            setMessageOpen(parentMessage.id, open)
          }
          senderIsFormerMember={
            !!activeWorkspaceMemberIds &&
            !activeWorkspaceMemberIds.has(parentMessage.user_id)
          }
        />
        <div className="h-px bg-border my-2" />
        <ThreadReplyList
          orderedReplies={orderedReplies}
          replyCount={threadReplies.length}
          isPublicWs={isPublicWs}
          parentMessageId={parentMessageId}
          acceptedAnswerId={acceptedAnswerId}
          canCurate={canCurate}
          sharedUserMap={sharedUserMap}
          workspaceMembers={workspaceMembers}
          channels={channels}
          onUserClick={onUserClick}
          onChannelClick={onChannelClick}
          onSendToShell={onSendToShell ? handleThreadSendToShell : undefined}
          highlightedReplyId={highlightedReplyId}
          isMessageOpen={isMessageOpen}
          setMessageOpen={setMessageOpen}
          activeWorkspaceMemberIds={activeWorkspaceMemberIds}
        />
      </div>

      <MessageInput
        channelId={channelId}
        channelName={channels?.find((c) => c.id === channelId)?.name}
        threadId={parentMessageId}
        workspaceMembers={workspaceMembers}
        isCoworkerDm={isCoworkerDm}
        coworkerIds={coworkerIds}
        hidden={hidden}
      />
    </div>
  );
}
